
import React from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';
import { useIsMobile } from '@/hooks/use-mobile';
import { useScrollPosition } from '@/hooks/useScrollPosition';
import { ChevronDown } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import HorizontalScroller from '@/components/mobile/HorizontalScroller';
import { Button } from '@/components/ui/button';

interface BrowseSidebarProps {
  categories: string[];
  activeCategory: string;
  onCategoryChange: (category: string) => void;
  categoryCounts?: Record<string, number>;
}

const BrowseSidebar: React.FC<BrowseSidebarProps> = ({ 
  categories, 
  activeCategory, 
  onCategoryChange,
  categoryCounts = {}
}) => {
  const { resolvedTheme } = useTheme();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const isScrolledMobile = useScrollPosition({ threshold: 1 });
  
  const textColor = resolvedTheme === 'dark' ? 'text-white' : 'text-black';
  const mutedTextColor = resolvedTheme === 'dark' ? 'text-white/70' : 'text-black/70';
  const hoverBgColor = resolvedTheme === 'dark' ? 'hover:bg-white/10' : 'hover:bg-black/5';
  const activeBgColor = resolvedTheme === 'dark' ? 'bg-white/15' : 'bg-white';

  // Helper function to convert string to sentence case
  const toSentenceCase = (str: string): string => {
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  const handleCategoryClick = (category: string) => {
    // Update local state
    onCategoryChange(category);
    
    // Update URL
    if (category === 'all') {
      navigate('/browse');
    } else {
      navigate(`/browse?category=${category}`);
    }
  };
  
  // Mobile tags version
  if (isMobile) {
    return (
      <>
        {isScrolledMobile && <div className="h-12" />}
        <div
          className={cn(
            "w-full mb-6 py-0",
            isScrolledMobile
              ? "fixed top-16 left-0 right-0 z-40 bg-background shadow-sm"
              : "sticky top-16 z-40 bg-background"
          )}
        >
          <HorizontalScroller noLeftPadding={true} showNextCard={false} spacing="sm" disableScale>
            {categories.map((category) => (
              <Button
                key={category}
                variant={activeCategory === category ? "secondary" : "outline"}
                size="sm"
                onClick={() => handleCategoryClick(category)}
                className={cn(
                  "whitespace-nowrap rounded-full border hover:bg-muted transition-colors",
                  "bg-transparent hover:text-foreground cursor-pointer font-normal flex-shrink-0",
                  activeCategory === category ? "bg-secondary text-secondary-foreground font-medium" : ""
                )}
              >
                {toSentenceCase(category)}
                {categoryCounts[category] !== undefined && (
                  <span className="ml-2 opacity-70">
                    {categoryCounts[category]}
                  </span>
                )}
              </Button>
            ))}
          </HorizontalScroller>
        </div>
      </>
    );
  }

  // Desktop sidebar version
  return (
    <div className="sidebar-sticky">
      <ul className="space-y-1">
        {categories.map((category) => (
          <li key={category}>
            <button
              onClick={() => handleCategoryClick(category)}
              className={cn(
                "w-full text-left py-2 px-3 rounded-md transition-colors flex justify-between items-center",
                activeCategory === category ? activeBgColor : 'bg-transparent',
                activeCategory === category ? textColor : mutedTextColor,
                hoverBgColor
              )}
            >
              <span>{toSentenceCase(category)}</span>
              {categoryCounts[category] !== undefined && (
                <span className={cn("text-xs", mutedTextColor)}>
                  {categoryCounts[category]}
                </span>
              )}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default BrowseSidebar;
